import type { ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { cn } from '../lib/cn';

export type AppNavItem = {
  to: string;
  label: string;
  icon?: ReactNode;
  end?: boolean;
  /** Show only when > 0 (primary-area counts). */
  count?: number;
};

export type AppNavProps = {
  items: AppNavItem[];
  className?: string;
  /** Collapsed rail shows icons only (product primary can use later). */
  collapsed?: boolean;
};

/**
 * Primary product navigation — light surface, soft brand fill when selected.
 * Prefer this over dark chrome for new product surfaces.
 */
export function AppNav({ items, className, collapsed }: AppNavProps) {
  return (
    <nav
      aria-label="Primary"
      className={cn('flex flex-col gap-0.5 p-2', className)}
    >
      {items.map(item => (
        <NavLink
          key={item.to}
          to={item.to}
          {...(item.end != null ? { end: item.end } : {})}
          {...(collapsed ? { title: item.label } : {})}
          className={({ isActive }) =>
            cn(
              'flex items-center gap-2 rounded-ds-md px-2.5 py-1.5 text-ds-sm font-medium',
              'transition-colors duration-ds-fast ease-ds',
              collapsed && 'justify-center px-2',
              isActive
                ? 'bg-ds-brand-subtle text-ds-brand'
                : 'text-ds-text-secondary hover:bg-ds-subtle hover:text-ds-text',
            )
          }
        >
          {item.icon ? (
            <span className="inline-flex shrink-0 [&_svg]:size-4">{item.icon}</span>
          ) : null}
          {!collapsed ? <span className="min-w-0 flex-1 truncate">{item.label}</span> : null}
          {!collapsed && item.count != null && item.count > 0 ? (
            <span className="tabular-nums text-ds-xs text-ds-muted">{item.count}</span>
          ) : null}
        </NavLink>
      ))}
    </nav>
  );
}
